<template>
  <v-layout class="d-flex flex-column h-screen">
    <v-main :class="containerState" class="d-flex flex-grow-1">
      <v-container id="main-container-definition" fluid>
        <v-overlay v-model="overlay" class="align-center justify-center" contained opacity="1" scrim="white">
          <v-progress-circular indeterminate size="64" color="secondary"></v-progress-circular>
        </v-overlay>
        <v-card class="h-100 d-flex flex-column" flat>
          <v-card-text class="pa-0 mx-auto d-flex justify-center overflow-y-auto scrollable-content"
            :opacity="opacityContent">
            <v-sheet max-width="1280" width="100%">
              <v-row class="h-100" dense>
                <v-col cols="4" class="d-flex flex-column" v-if="!isAdminCommon">
                  <DescriptionCard :containerHeight="containerHeight" :parentDescription="sessionItem.description"
                    @updateDescription="updateDescription"></DescriptionCard>
                  <GeolocationCard :containerHeight="containerHeight"
                    :parentGeolocation="sessionItem.definition.geolocation" @updateGeolocation="updateGeolocation">
                  </GeolocationCard>
                </v-col>
                <v-col :cols="isAdminCommon ? 12 : 8">
                  <v-row>
                    <v-col cols="12">
                      <KpiTypeSelector :model-value="sessionItem.definition.valueType" :disabled="isTypeLocked"
                        @request-change="onRequestTypeChange" />
                    </v-col>
                    <v-col cols="6">
                      <KpiAllowedValues v-model="sessionItem.definition.allowedValues" @reorder="updateSelectedItem"
                        :disabled="sessionItem.definition.valueType === 'QUANTITATIVE' || isTypeLocked
                          " />
                    </v-col>
                    <v-col cols="6" v-if="!isAdminCommon">
                      <KpiValuesInput v-model="sessionItem.definition.manualInput"
                        :value-type="sessionItem.definition.valueType"
                        :allowed-values="sessionItem.definition.allowedValues" />
                    </v-col>
                    <v-col :cols="isAdminCommon ? 6 : 12"
                      v-if="!isAdminCommon || sessionItem.definition.valueType === 'QUANTITATIVE'">
                      <KpiTargetDefinition :symbolsUnitsMeasures="mfConfig.unitSymbols"
                        :parentTargetValue="sessionItem.definition.targetValue"
                        :parentUnitMeasure="sessionItem.definition.unitMeasure"
                        :value-type="sessionItem.definition.valueType"
                        :allowed-values="sessionItem.definition.allowedValues" :hide-target-value="isAdminCommon"
                        :disabled-unit-measure="isTypeLocked" @writeTargetValue="writeTargetValue"
                        @writeUnitMeasure="writeUnitMeasure">
                      </KpiTargetDefinition>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-sheet>
          </v-card-text>
          <v-card-actions class="pb-0" :class="!overlay ? 'd-flex' : 'd-none'">
            <v-btn variant="outlined" color="secondary" class="me-auto px-2" @click="routeToHome">
              {{ $t("labels.cancel") }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn :disabled="!canContinue" variant="elevated" color="secondary" class="ml-2 text-white px-2"
              @click="routeToFormula">
              <span v-if="sessionItem.id">{{ $t("labels.update") }}</span>
              <span v-else-if="isAdminCommon">{{ $t("labels.save") }}</span>
              <span v-else>{{ $t("labels.continue") }}</span>
            </v-btn>
          </v-card-actions>
          <v-dialog v-model="typeConfirmDialog" width="450" persistent>
            <v-card>
              <v-card-title class="text-h6 text-primary">{{
                $t("strings.confirm-title")
              }}</v-card-title>
              <v-card-text>
                {{ $t("strings.values-type-change-confirm") }}
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="cancelTypeChange">{{
                  $t("labels.cancel")
                }}</v-btn>
                <v-btn color="secondary" text @click="confirmTypeChange">{{
                  $t("labels.confirm")
                }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </v-container>
    </v-main>
  </v-layout>
</template>

<script>
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";
import mapMethods from "../mixins/mapMethods";
import DescriptionCard from "../components/DescriptionCard.vue";
import GeolocationCard from "../components/GeolocationCard.vue";
import KpiAllowedValues from "../components/KpiAllowedValues.vue";
import KpiTypeSelector from "../components/KpiTypeSelector.vue";
import KpiTargetDefinition from "../components/KpiTargetDefinition.vue";
import KpiValuesInput from "../components/KpiValuesInput.vue";
import axios from "axios";
export default {
  name: "demf-kpim-editor-definition",
  mixins: [events, utils, mapMethods],
  props: ["mfConfig", "realm"],
  components: {
    DescriptionCard,
    GeolocationCard,
    KpiAllowedValues,
    KpiTypeSelector,
    KpiTargetDefinition,
    KpiValuesInput,
  },
  data: function () {
    return {
      overlay: true,
      apiUrl: this.mfConfig.api,
      containerState: "expanded",
      loggedUserId: "",
      loading: false,
      itemsSelected: [],
      sessionItem: JSON.parse(sessionStorage.getItem("kpi")),
      // dialog state and pending values for confirming type change
      typeConfirmDialog: false,
      pendingTypeOld: null,
      pendingTypeNew: null,
      containerHeight: "0px",
      opacityContent: 0,
      roles: [],
      loadingRoles: true,
    };
  },

  created() {
    this.overlay = true;
  },

  async mounted() {
    this.loadingRoles = true;
    await this.getRoles();
    this.loadingRoles = false;
    this.loggedUserId = localStorage.getItem("loggedUserId");

    // Normalize/migrate sessionItem.definition to the new structure:
    // - `allowedValues` replaces `selected`
    // - `manualInput` replaces `values` (array of {value,timestamp})
    // - `valueType` replaces `type` and uses 'QUALITATIVE'|'QUANTITATIVE'
    if (!this.sessionItem) {
      this.sessionItem = {
        definition: {
          allowedValues: [],
          manualInput: [],
          valueType: "QUANTITATIVE",
        },
      };
    } else {
      if (!this.sessionItem.definition) this.sessionItem.definition = {};

      // migrate selected -> allowedValues
      if (
        Array.isArray(this.sessionItem.definition.selected) &&
        !Array.isArray(this.sessionItem.definition.allowedValues)
      ) {
        this.sessionItem.definition.allowedValues = [
          ...this.sessionItem.definition.selected,
        ];
      }
      if (!Array.isArray(this.sessionItem.definition.allowedValues))
        this.sessionItem.definition.allowedValues = [];

      // migrate values -> manualInput (preserve values, add timestamps)
      if (
        Array.isArray(this.sessionItem.definition.values) &&
        !Array.isArray(this.sessionItem.definition.manualInput)
      ) {
        const now = new Date().toISOString();
        this.sessionItem.definition.manualInput =
          this.sessionItem.definition.values.map((v) => ({
            value: v,
            timestamp: now,
          }));
      }
      if (!Array.isArray(this.sessionItem.definition.manualInput))
        this.sessionItem.definition.manualInput = [];

      // migrate type -> valueType
      if (this.sessionItem.definition.type === "string") {
        this.sessionItem.definition.valueType = "QUALITATIVE";
      } else if (this.sessionItem.definition.type === "numeric") {
        this.sessionItem.definition.valueType = "QUANTITATIVE";
      } else if (!this.sessionItem.definition.valueType) {
        this.sessionItem.definition.valueType = "QUANTITATIVE";
      }

      // clean up legacy keys to avoid confusion elsewhere
      if (this.sessionItem.definition.selected)
        delete this.sessionItem.definition.selected;
      if (this.sessionItem.definition.values)
        delete this.sessionItem.definition.values;
      if (this.sessionItem.definition.type)
        delete this.sessionItem.definition.type;
      // migrate top-level `type` -> `kpiType` to change stored JSON label
      if (this.sessionItem.type && !this.sessionItem.kpiType) {
        this.sessionItem.kpiType = this.sessionItem.type;
        delete this.sessionItem.type;
      }
    }

    document.addEventListener("language-localstorage-changed", (event) => {
      this.setLocale(event.detail.storage);
    });

    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener("sidenav-localstorage-changed", (event) => {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    });

    this.$nextTick(() => {
      this.updateContainerHeight(); // Update dimensions
      window.addEventListener("resize", this.updateContainerHeight);
      // hide overlay and show content after initial render
      this.overlay = false;
      this.opacityContent = 1;
    });

    // Note: type change is now requested by child; parent will show confirmation dialog
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateContainerHeight);
  },

  watch: {
    sessionItem: {
      handler(newVal) {
        try {
          sessionStorage.setItem("kpi", JSON.stringify(newVal));
        } catch (e) {
          console.error("Failed to persist sessionItem to sessionStorage", e);
        }
      },
      deep: true,
    },
  },

  computed: {
    isAdmin() {
      return this.roles && this.roles.includes('admin');
    },
    isCommonKPI() {
      return !!(this.sessionItem && (this.sessionItem.commonKpi || this.sessionItem.commonKpiMasterId));
    },
    isAdminCommon() {
      return this.isAdmin && this.isCommonKPI;
    },
    // Rules for button activation based on user role and KPI type
    canContinue() {
      const def = this.sessionItem?.definition || {};

      // 1. Admin Flow
      if (this.isAdminCommon) {
        if (def.valueType === "QUALITATIVE") {
          return Array.isArray(def.allowedValues) && def.allowedValues.length > 0;
        }
        if (def.valueType === "QUANTITATIVE") {
          return !!def.unitMeasure;
        }
        return false;
      }


      // 2. Editor Flow f
      const hasValues = Array.isArray(def.manualInput) && def.manualInput.length > 0;
      const hasTarget = def.targetValue !== null && def.targetValue !== undefined && def.targetValue !== "";
      if (!hasValues || !hasTarget) return false;

      if (def.valueType === "QUANTITATIVE") {
        return def.unitMeasure !== null && def.unitMeasure !== undefined && def.unitMeasure !== "";
      }
      return true;
    },
    isEditMode() {
      return !!(this.sessionItem && this.sessionItem.id);
    },
    // Determines if structural fields (Type, Allowed Values, Unit) are locked
    isTypeLocked() {
      // Editor instantiating a Common KPI from notification: locked (inherits master structure)
      if (this.isCommonKPI && !this.isAdminCommon) {
        return true;
      }
      // Standard non-common KPI in edit mode: locked as per legacy behavior
      if (!this.isCommonKPI && this.isEditMode) {
        return true;
      }
      // Unlocked for Admin creating Common KPI or Editor creating standard non-common KPI
      return false;
    },
  },

  methods: {
    async getRoles() {
      try {
        const response = await axios.get(`${this.apiUrl}/${this.realm}/me`, { withCredentials: true });
        const rawRoles = response.data.roles || [];
        this.roles = rawRoles.map(role => role.replace(/^kpim-/, ''));
      } catch (error) {
        console.error("Error fetch roles:", error);
      }
    },
    onRequestTypeChange(newType) {
      // parent opens confirmation dialog before applying the type change
      if (!this.sessionItem || !this.sessionItem.definition) return;
      this.pendingTypeOld = this.sessionItem.definition.valueType;
      this.pendingTypeNew = newType;
      // Only open dialog if there are existing values/allowed values to clear; otherwise apply immediately
      const hasManualInput =
        Array.isArray(this.sessionItem.definition.manualInput) &&
        this.sessionItem.definition.manualInput.length > 0;
      const hasAllowed =
        Array.isArray(this.sessionItem.definition.allowedValues) &&
        this.sessionItem.definition.allowedValues.length > 0;
      if (hasManualInput || hasAllowed) {
        this.typeConfirmDialog = true;
      } else {
        // apply immediately
        this.sessionItem.definition.valueType = newType;
        // clear target/unit as safety
        this.sessionItem.definition.targetValue = null;
        this.sessionItem.definition.unitMeasure = null;
      }
    },
    confirmTypeChange() {
      // user confirmed: apply pending type, clear values and close dialog
      if (this.sessionItem && this.sessionItem.definition) {
        // apply the new type
        if (this.pendingTypeNew) {
          this.sessionItem.definition.valueType = this.pendingTypeNew;
        }
        // clear manual input and allowed values
        this.sessionItem.definition.manualInput = [];
        this.sessionItem.definition.allowedValues = [];
        // clear target value and unit measure as they may no longer apply
        this.sessionItem.definition.targetValue = null;
        this.sessionItem.definition.unitMeasure = null;
      }
      this.typeConfirmDialog = false;
      this.pendingTypeOld = null;
      this.pendingTypeNew = null;
    },
    cancelTypeChange() {
      // user cancelled: close dialog and discard pending change
      this.typeConfirmDialog = false;
      this.pendingTypeOld = null;
      this.pendingTypeNew = null;
    },
    setEditableStep(step, value) {
      document.dispatchEvent(
        new CustomEvent("editable-steps", {
          detail: { step, value },
        }),
      );
    },

    toggleOverlay(value) {
      this.opacityContent = value ? 0 : 1;
      this.overlay = value;
    },

    //Calculate tag container height
    updateContainerHeight() {
      const element = document.getElementById("main-container-definition");
      if (element) {
        const elementCss = window.getComputedStyle(element);
        const paddingTop = parseFloat(
          elementCss.getPropertyValue("padding-top"),
        );
        const paddingBottom = parseFloat(
          elementCss.getPropertyValue("padding-bottom"),
        );
        this.containerHeight =
          element.offsetHeight - paddingTop - paddingBottom + "px";
      } else {
        console.warn("Not Found: ", element);
      }
    },
    //Redirect to Kpis list
    routeToHome() {
      this.$router.push({ path: "/kpis" });
    },
    //Updates sessionStorage, updates Editor step and continue to Formula page
    routeToFormula() {
      sessionStorage.setItem("kpi", JSON.stringify(this.sessionItem));
      if (this.isAdminCommon) {
        this.createCommonKPI();
        return;
      }
      this.setEditableStep("formula", 2);
      this.$router.push({ path: "/editor/dashboard" });
    },

    createCommonKPI() {
      this.overlay = true;
      const def = this.sessionItem.definition || {};
      const base = {
        name: this.sessionItem.name,
        code: this.sessionItem.code,
        category: def.category?.join(', ') || '',
        description: this.sessionItem.description,
        type: "MANUAL",
        unitMeasure: def.unitMeasure,
      };
      const payload = def.valueType === "QUALITATIVE"
        ? { ...base, valueType: "QUALITATIVE", allowedValues: def.allowedValues || [] }
        : { ...base, valueType: "QUANTITATIVE" };

      axios
        .post(`${this.apiUrl}/${this.realm}/kpi/common`, payload, { withCredentials: true })
        .then(() => {
          sessionStorage.removeItem("kpi");
          this.$router.push({ path: "/kpis" });
        })
        .catch((err) => {
          this.setError(err, false);
          this.overlay = false;
        });
    },

    updateGeolocation(newGeolocation) {
      this.sessionItem.definition.geolocation = { ...newGeolocation };
    },
    updateDescription(newDescription) {
      this.sessionItem.description = newDescription;
    },
    updateSelectedItem(items) {
      this.itemsSelected = items;
      this.sessionItem.definition.allowedValues = items;
    },
    writeTargetValue(targetValue) {
      this.sessionItem.definition.targetValue = targetValue;
    },
    writeUnitMeasure(unitMeasure) {
      this.sessionItem.definition.unitMeasure = unitMeasure;
    },
  },
};
</script>
<style scoped>
main.expanded {
  margin-left: 56px;
}

main.reduced {
  margin-left: 256px;
}

#main-container-definition {
  background-color: white;
}

.scrollable-content {
  max-height: calc(100vh - 280px);
  width: 100%;
}
</style>
